/**
 * Frontend-owned shapes for the omnium-admin REST responses. Hand-authored and
 * decoupled from the Python packages — talk to the backend via APIs only. The
 * admin BFF emits camelCase (matching the metadata document + envelope contract).
 */

/** The 14-type field catalog (mirrors omnium_core.metadata.FieldType). */
export type FieldType =
  | "number"
  | "integer"
  | "ratio"
  | "text"
  | "boolean"
  | "enum"
  | "duration"
  | "date"
  | "datetime"
  | "ref"
  | "object"
  | "array"
  | "country"
  | "richtext";

export type EnumOption = { value: string | number; label?: string };

/** Presentation hints an author can attach to a field. `widget` overrides the
 * catalog's default input for the field's type. */
export type UiHints = { widget?: string; help?: string; order?: number };

/** One authored field (flat; per-type constraints optional). */
export type FieldDef = {
  key: string;
  label?: string;
  type: FieldType;
  required?: boolean;
  ui?: UiHints;
  // constraints (only the ones the form engine reads are typed loosely here)
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  asPercent?: boolean;
  options?: EnumOption[];
  multiple?: boolean;
  /** A physical kind ("participant") or a configured master ("master:team"). */
  target?: string;
  participantType?: string;
  /** Key of a sibling field whose value scopes this field's options. */
  dependsOn?: string;
  maxLength?: number;
  pattern?: string;
};

/** Prefix marking a ref target as a configured master (mirrors the core catalog). */
export const MASTER_PREFIX = "master:";

/** The master code a ref target names, or null when it names a physical kind. */
export function masterCodeOf(target: string | undefined): string | null {
  return target?.startsWith(MASTER_PREFIX)
    ? target.slice(MASTER_PREFIX.length)
    : null;
}

/** A temporal grain in the sport's vocabulary. */
export type UnitDef = { unit: string; label: string; nests?: string };

/** In-play field-sets: whole-fixture fields + per-period fields keyed by unit. */
export type LiveStateDoc = {
  fields: FieldDef[];
  periods: Record<string, { fields: FieldDef[] }>;
};

/** Two sides meeting, or a field of entrants ranked against each other. The
 * operator never sees the word "archetype"; this is what they answer. */
/** One person attached to a fixture through a named role. */
export type RoleSlot = {
  master: string;
  role: string;
  label: string;
  required?: boolean;
  /**
   * How many appointments this slot takes. The form draws a picker per
   * appointment, so without these it can only ever offer one person per role —
   * and a syndicate of owners or a panel of three judges becomes unenterable.
   * `max` of null means no ceiling.
   */
  min?: number;
  max?: number | null;
};

/** A sport-definition document (GET /definitions/{sport}, and the builder's POST body). */
export type SportDefinitionDoc = {
  sportCode: string;
  /** The sport this one sits inside, by code — Aquatics holds swimming, diving
   * and water polo. A child inherits its parent's definition and adds to it. */
  partOf?: string | null;
  version?: number;
  status?: string;
  /**
   * Field sets keyed by where they live — "fixture.format",
   * "fixtureCompetitor.metric". Optional because a document only carries the
   * sections somebody set: the API serialises with `exclude_unset`, so a sport
   * that never declared one has no key at all rather than an empty object.
   */
  entities?: Record<string, FieldDef[]>;
  units?: UnitDef[];
  liveState?: LiveStateDoc;
  activeRoleCodes?: string[];
  /** The operator-defined masters (docs/design/dynamic-masters.md). */
  masters?: MasterDefinition[];
  /** Which competition-bound master new fixtures attach under. */
  fixtureParent?: string | null;
  /** The kinds of fixture this sport holds. */
  fixtureTypes?: FixtureTypeDef[];
  /** The same kind of fixture played to different rules — T20 and Test, sevens
   * and fifteens. Kept apart from the kinds because the two multiply. */
  configs?: ConfigDef[];
  /**
   * The id of an entry in the icon catalogue (`lib/sportIcons`), shown wherever
   * this sport appears. An emoji id like `cricket`, not a component name.
   */
  icon?: string | null;
  /**
   * Whether it holds disciplines rather than fixtures — Aquatics holds swimming,
   * diving and water polo, and nothing is ever entered into Aquatics itself.
   * A grouping declares no kinds of fixture, and the model refuses a document
   * that carries both.
   */
  groups?: boolean;
};

// --- fixture entry ---------------------------------------------------------

/** One selectable record in a generated dropdown. */
export type FormOption = { code: string; name: string; parent?: string | null };

export type LevelOptions = {
  master: string;
  label: string;
  options: FormOption[];
};

/** One place a fixture may be filed — every competition node whose level allows
 *  fixtures, with its full path so a single picker is enough. */
export type Container = {
  code: string;
  name: string;
  path: string;
  level: string;
};

export type SlotOptions = RoleSlot & { min: number; options: FormOption[] };

/** One kind of fixture a sport holds, as the picker sees it. */
export type FixtureTypeOption = {
  code: string;
  label: string;
  outcome: OutcomeModel;
};

export type OutcomeModel = "DUEL" | "FIELD" | "SERIES" | "COMPOSITE";

/** One card on the new-fixture picker: a kind of fixture, played to a config.
 * The two are separate underneath, but nobody adding a cricket match picks
 * "Match" and then "T20" — so the cross product is offered flat. */
export type FixtureChoice = {
  fixtureType: string;
  config: string | null;
  label: string;
  outcome: OutcomeModel;
  values: Record<string, unknown>;
};

/** Who each entry fields, and in what order. */
export type CompositionSpec = {
  master: string;
  min?: number | null;
  max?: number | null;
  ordered?: boolean;
  /** Named places ("Lead", "Anchor"); implies an order and an exact count. */
  slots?: string[];
  from?: "entrantRoster" | "anyOfMaster";
  fields?: FieldDef[];
  roles?: string[];
};

/** The fixture type the screen is rendering — what it asks for and produces. */
export type FixtureTypeDef = {
  code: string;
  label: string;
  outcome: OutcomeModel;
  entrant: { master: string; min?: number | null; max?: number | null };
  composition?: CompositionSpec | null;
  alignments?: string[];
  entryFields?: FieldDef[];
  resultFields?: FieldDef[];
  /** The rules of the contest. A config writes these. */
  format?: FieldDef[];
  /** This fixture in particular — the going, the attendance, the match number,
   * the belts a bout is for. Typed by whoever enters it, never by a config. */
  meta?: FieldDef[];
  periods?: PeriodDef[];
  rankings?: { code: string; label: string; rule: string }[];
  cut?: { advance?: number | null; by: string; rule?: string | null } | null;
  series?: {
    rounds: { key: string; label: string; attempts?: number | null }[];
    declares?: FieldDef[];
    outcomes?: EnumOption[];
  } | null;
};

/** GET /sports/{sport}/fixture-form — everything the entry screen needs. */
export type FixtureFormContext = {
  sportCode: string;
  /** Which type this context describes; null when the operator must choose. */
  fixtureType: string | null;
  fixtureTypes: FixtureTypeOption[];
  /** Which config it is played to, and every card the picker offers. */
  config: string | null;
  choices: FixtureChoice[];
  typeDef: FixtureTypeDef | null;
  entrantLabel: string;
  lineupLabel: string | null;
  levels: LevelOptions[];
  containers: Container[];
  entrants: FormOption[];
  lineup: FormOption[];
  connections: SlotOptions[];
  officials: SlotOptions[];
  venues: FormOption[];
  formatFields: FieldDef[];
  /** Every published scoring program, for the operator to pick from. */
  scoringPrograms: {
    id: string;
    version: number;
    name: string | null;
    scope: string[];
  }[];
  /** The picker's default — the program whose scope best matches the chosen
   *  kind and config. Never a silent assignment. */
  suggestedScoringProgram: string | null;
  /** Registered external scoring surfaces that could open this fixture. */
  scoringUis: { id: string; name: string; url: string; appliesTo: string[] }[];
  suggestedScoringUi: string | null;
  /** Known before the contest (lane, stall, grid) and known after (the figures). */
  entryFields: FieldDef[];
  resultFields: FieldDef[];
  metricFields: FieldDef[];
};

/** Row from GET /definitions. */
/** One level of a sport's competition tree. */
export type LevelRow = { code: string; label: string };

export type DefinitionRow = {
  sportCode: string;
  version: number;
  status: string;
  publishedAt?: string | null;
  /** The sport this one sits inside — Aquatics holds swimming. */
  partOf?: string | null;
  /** A grouping: it holds disciplines and hosts nothing itself, so it has no
   *  fixture to enter and must not be offered one. */
  groups?: boolean;
  /** What this sport may start its tree as. Empty for a grouping sport,
   *  which therefore cannot hold a Games programme itself. */
  topLevels?: LevelRow[];
};

/** Result of creating or publishing a definition version. */
export type DefinitionVersionResult = {
  sportCode: string;
  version: number;
  status: string;
};

/** Row from GET /fixtures. */
export type FixtureRow = {
  id: string;
  code: string;
  name?: string | null;
  sportCode: string;
  archetype: string;
  status: string;
  scheduledStart?: string | null;
  /** The container it is filed under ("Matchweek 3"). */
  competition?: string | null;
  /** Formatted server-side — a client component formatting a date in the
   * browser's locale breaks hydration. */
  startsLabel?: string | null;
};

/** GET /fixtures — one page plus the total behind it. */
export type FixturePage = { rows: FixtureRow[]; total: number };

export type FixtureQuery = {
  /** A window, for "what is on today" — it crosses every sport. */
  from?: string;
  to?: string;
  /** 'soonest' for a day being worked through, 'newest' for browsing. */
  order?: "soonest" | "newest";
  sport?: string;
  q?: string;
  status?: string;
  limit?: number;
  offset?: number;
};

/** Row from GET /fixtures/counts. */
export type FixtureCount = { sportCode: string; total: number };

/** Row from GET /participants. */
export type ParticipantRow = {
  id?: string;
  code: string;
  name: string;
  type: string;
  country?: string | null;
};

/** GET /participants/{id} — full master record. */
export type ParticipantDetail = {
  id: string;
  sportCode: string;
  code: string;
  type: string;
  name: string;
  country?: string | null;
  meta: Record<string, unknown>;
};

export type ParticipantCreatePayload = {
  sportCode: string;
  type: string;
  name: string;
  code?: string;
  country?: string;
  meta?: Record<string, unknown>;
};

export type ParticipantUpdatePayload = {
  name?: string;
  country?: string;
  meta?: Record<string, unknown>;
};

/** Row from GET /roles. */
export type RoleRow = {
  id?: string;
  code: string;
  name: string;
  category: string;
};

export type RoleDetail = {
  id: string;
  sportCode?: string | null;
  code: string;
  name: string;
  category: string;
  description?: string | null;
};

export type RoleCreatePayload = {
  code: string;
  category: string;
  name: string;
  sportCode?: string;
  description?: string;
};

export type RoleUpdatePayload = {
  name?: string;
  category?: string;
  description?: string;
};

// --- Competition ---
export type CompetitionRow = {
  id: string;
  code: string;
  type: string;
  name: string;
  status?: string | null;
};

export type CompetitionDetail = {
  id: string;
  sportCode: string;
  code: string;
  type: string;
  name: string;
  status?: string | null;
  ordinal?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  parentCode?: string | null;
  config: Record<string, unknown>;
};

export type CompetitionCreatePayload = {
  sportCode: string;
  type: string;
  name: string;
  code?: string;
  status?: string;
  ordinal?: number;
  startDate?: string;
  endDate?: string;
  parentCode?: string;
  config?: Record<string, unknown>;
};

export type CompetitionUpdatePayload = Omit<
  CompetitionCreatePayload,
  "sportCode" | "code"
>;

// --- Venue ---
export type VenueRow = {
  id: string;
  code: string;
  name: string;
  country?: string | null;
  city?: string | null;
};

export type VenueDetail = VenueRow & { meta: Record<string, unknown> };

export type VenueCreatePayload = {
  name: string;
  code?: string;
  country?: string;
  city?: string;
  meta?: Record<string, unknown>;
};

export type VenueUpdatePayload = {
  name?: string;
  country?: string;
  city?: string;
  meta?: Record<string, unknown>;
};

/** A support connection on an entry (jockey/trainer). */
export type ConnectionInput = { role: string; participant: string };

/** One competitor in a fixture (recursive: children = a team's lineup).
 *
 * Either a concrete ``participant`` or a labelled TBD ``placeholderLabel`` —
 * exactly one. A placeholder is an entrant slot with no party yet ("Winner of
 * QF1"), resolved later by editing the fixture. */
export type EntryInput = {
  participant?: string;
  /** A labelled TBD slot instead of a concrete entrant. */
  placeholderLabel?: string;
  rank?: number;
  resultStatus?: string;
  alignment?: string;
  /** Position among siblings, and the named place it fills. */
  ordinal?: number;
  slot?: string;
  /** The draw — known before the contest. Kept apart from the figures. */
  entry?: Record<string, unknown>;
  result?: Record<string, unknown>;
  metric?: Record<string, unknown>;
  connections?: ConnectionInput[];
  roles?: string[];
  /** Who this entry fields. ``children`` is the pre-revamp name for the same thing. */
  members?: EntryInput[];
  children?: EntryInput[];
};

/** One appointed official on a fixture. */
export type OfficialInput = { role: string; participant: string };

/** POST /fixtures request body. */
export type FixtureCreatePayload = {
  /** Which config it was played to — the rules are copied server-side. */
  formatCode?: string;
  /** The published scoring program the operator picked; pinned at entry. */
  scoringProgram?: string;
  /** The registered scoring surface the console opens for this fixture. */
  scoringUi?: string;
  sportCode: string;
  code: string;
  /** Which kind of fixture this is. Omitted when the sport holds only one. */
  fixtureType?: string;
  /** Legacy: superseded by fixtureType. */
  archetype?: string;
  entries: EntryInput[];
  /** The container node this fixture hangs under. */
  competition?: string;
  venue?: string;
  officials?: OfficialInput[];
  coverageTier?: string;
  status?: string;
  name?: string;
  scheduledStart?: string;
  format: Record<string, unknown>;
  result?: Record<string, unknown> | null;
  liveState?: Record<string, unknown> | null;
};

export type FixtureCreated = { id: string; code: string };

/** PATCH /fixtures/{id} request body: a partial edit. Only the fields present
 *  are changed. ``entries`` replaces the whole entrant set (how a TBD is
 *  resolved and how competitors are corrected). */
export type FixtureAmendPayload = {
  name?: string | null;
  scheduledStart?: string | null;
  venue?: string | null;
  competition?: string | null;
  meta?: Record<string, unknown> | null;
  entries?: EntryInput[];
  reason?: string;
};

/** One row on a fixture's append-only amendment history. */
export type AmendmentRow = {
  id: string;
  seq: number;
  kind: "AMEND" | "CORRECTION";
  statusAt: string;
  changes: Record<string, unknown>;
  reason: string | null;
  createdAt: string;
};

// --- Dynamic masters (docs/design/dynamic-masters.md) ---

export type BindingKind =
  | "competition-node"
  | "participant"
  | "venue"
  | "transfer"
  | "name-history"
  | "media"
  | "outcome-codes";

export type MasterBinding = {
  kind: BindingKind;
  participantType?: string;
  target?: string;
  codeType?: string;
  /** competition-node only — the rules that turn a free tree into a guided one.
   * Every shape obeying them is legal, and nothing else is offered. */
  canBeTop?: boolean;
  /** Which levels may sit directly inside this one, by master code. */
  holds?: string[];
  /** May fixtures hang here. Several levels of one sport can: a cricket tour
   * holds warm-ups directly *and* holds series that hold Tests. */
  hasFixtures?: boolean;
};

/** One rung of a fixture's internal structure. */
export type PeriodDef = {
  key: string;
  label: string;
  nests?: string | null;
  ownedBy?: "fixture" | "entry";
  count?: number | null;
  /** An upper bound instead of an exact number — a Test is *up to* two innings
   * a side, fewer after a follow-on. */
  maxCount?: number | null;
  bestOf?: number | null;
};

/** One rung of a fixture's structure, restated for a config. */
export type PeriodOverride = {
  key: string;
  count?: number | null;
  maxCount?: number | null;
  bestOf?: number | null;
};

/** The same kind of fixture, played to different rules. */
export type ConfigDef = {
  code: string;
  label: string;
  /** Which kinds of fixture this can be picked for. Empty means all of them —
   * which is why adding a kind of fixture can widen every config onto it. */
  appliesTo?: string[];
  hidden?: boolean;
  values?: Record<string, unknown>;
  addFields?: FieldDef[];
  dropFields?: string[];
  entrantMin?: number | null;
  entrantMax?: number | null;
  compositionMin?: number | null;
  compositionMax?: number | null;
  periods?: PeriodOverride[];
};

export type RelationDef = {
  kind: "parent" | "memberOf";
  master: string;
  label?: string;
  required?: boolean;
};

/** One operator-defined master, as authored in the sport definition. */
export type MasterDefinition = {
  code: string;
  label: string;
  binding: MasterBinding;
  fields?: FieldDef[];
  codeRule?: { from: string[]; separator?: string };
  uniqueBy?: string[][];
  relations?: RelationDef[];
  list?: { columns: string[] };
};

/** Row from GET /sports/{sport}/masters. */
export type MasterSummary = {
  code: string;
  label: string;
  bindingKind: BindingKind;
  /** False for bindings that are configurable but not yet writable. */
  writable: boolean;
  definition: MasterDefinition;
};

export type MasterMembership = {
  master: string;
  code: string;
  validFrom?: string | null;
  validTo?: string | null;
};

/** A master record. Intrinsics are typed; configured fields live in `values`. */
export type MasterRecord = {
  /** The sports that see it. Empty means every one of them. */
  sports?: string[];
  id: string;
  code: string;
  name: string;
  country?: string | null;
  city?: string | null;
  timezone?: string | null;
  status?: string | null;
  ordinal?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  parent?: string | null;
  memberships?: MasterMembership[] | null;
  values: Record<string, unknown>;
};

/** POST/PATCH body — the record without its server-assigned id. */
export type MasterRecordPayload = Omit<MasterRecord, "id" | "code"> & {
  code?: string;
};

/** One problem found in an uploaded CSV, addressed as the operator sees it. */
export type CsvIssue = { row: number; column?: string | null; message: string };

export type CsvReport = {
  columns: string[];
  rowCount: number;
  created: number;
  updated: number;
  issues: CsvIssue[];
  committed: boolean;
};

export type ParticipantRef = {
  name: string;
  code?: string;
  type?: string;
  connections?: Record<string, string>;
  roles?: string[];
  lineup?: ParticipantRef[];
  /** A TBD placeholder slot: ``name`` is the label and there is no ``code``. */
  placeholder?: boolean;
};

export type LeaderboardRow = {
  rank?: number | null;
  participant: ParticipantRef;
  metric?: Record<string, unknown> | null;
  resultStatus: string;
};

/** A venue as the envelope carries it. */
export type VenueRef = {
  code?: string;
  name?: string;
  country?: string | null;
  city?: string | null;
};

/** The assembled envelope (GET /fixtures/{id}) — loosely typed for display.
 * `sportCode` and `competition` are added by the admin BFF: the envelope is the
 * client contract and carries neither. */
export type EnvelopeView = {
  kind: "H2H" | "RANKED";
  status: string;
  resultsOfficial?: boolean;
  sportCode?: string;
  competition?: string | null;
  /** The container's code (not just its display name), for the edit form. */
  competitionCode?: string | null;
  /** Which kind of fixture this is, so the edit form loads the same shape. */
  fixtureType?: string | null;
  /** Which config it was played to. */
  formatCode?: string | null;
  /** Where this fixture sits, outermost first — "World Cup / 2027 / Super
   * Eights". Not showing it was a real failure: a fixture points at exactly one
   * node and nobody could see which. */
  path?: { code: string; name: string }[];
  fixture: {
    archetype: string;
    format: Record<string, unknown>;
    code?: string;
    name?: string;
    scheduledStart?: string | null;
    venue?: VenueRef | null;
  };
  participants?: ParticipantRef[];
  leaderboard?: LeaderboardRow[];
  [key: string]: unknown;
};

/** One node of a sport's competition tree, with what it is allowed to hold. */
export type TreeNode = {
  id: string;
  code: string;
  type: string;
  label: string;
  name: string;
  status?: string | null;
  ordinal?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  formatCode?: string | null;
  fixtureCount: number;
  /** Level codes that may sit directly inside this one. */
  canHold: string[];
  canHoldFixtures: boolean;
  /**
   * True for a node that is *not* one of this sport's — the multi-sport event a
   * programme sits inside. It is in the tree so the programme has a visible parent
   * rather than an annotation saying where it went, and flagged so the screen offers
   * none of this sport's actions on it: a Games belongs to no sport and is authored
   * on the events screen.
   */
  external?: boolean;
  children: TreeNode[];
};

export type CompetitionTree = {
  roots: TreeNode[];
  canStart: { code: string; label: string }[];
  /** False when the sport has not declared its shape — every level may then sit
   * anywhere, which is how sports defined before the rules keep working. */
  guided: boolean;
};

/** One record that is not one sport's alone. */
export type CommonsRow = {
  id: string;
  code: string;
  name: string;
  country?: string | null;
  master: string;
  /** The sports that see it. **Empty means every one of them.** */
  sports: string[];
};

export type CommonsPage = { rows: CommonsRow[]; total: number };

/** A multi-sport event — an Olympics, a Commonwealth Games. A competition with
 * no sport, holding one programme per sport that takes part. */
export type EventRow = {
  id: string;
  code: string;
  name: string;
  startDate?: string | null;
  endDate?: string | null;
  sports: number;
  fixtures: number;
};

/** One sport's programme inside a Games. */
export type ProgrammeRow = {
  id: string;
  code: string;
  name: string;
  sportCode: string;
  fixtures: number;
};

export type EventDetail = EventRow & { programmes: ProgrammeRow[] };

// --- Outbound delivery (publishing layer) --------------------------------
//
// The control plane Ops manages: who receives which feed, over what transport,
// reshaped how. Mirrors the admin `/delivery/*` router (camelCase JSON).

/** A feed consumer. `credentialRef` never appears here — a client holds none. */
export type DeliveryClient = {
  id: string;
  code: string;
  name: string;
  parentId?: string | null;
  isActive: boolean;
};

export type DeliveryClientCreatePayload = {
  code: string;
  name: string;
  parentId?: string | null;
  isActive?: boolean;
};

export type DeliveryClientUpdatePayload = {
  name?: string;
  parentId?: string | null;
  isActive?: boolean;
};

/** An entitlement product: the set of feed keys it grants. */
export type FeedPackage = {
  id: string;
  code: string;
  name: string;
  feedKeys: string[];
  isActive: boolean;
};

export type FeedPackageCreatePayload = {
  code: string;
  name: string;
  feedKeys?: string[];
  isActive?: boolean;
};

export type FeedPackageUpdatePayload = {
  name?: string;
  feedKeys?: string[];
  isActive?: boolean;
};

/**
 * A persisted feed query (`feed_definition`) — config, not code. Its `query` is
 * a GraphQL document run against the public schema; editing it changes what the
 * feed selects with no deploy. `code` is the handle a package's feed key names.
 */
export type FeedDefinition = {
  code: string;
  name: string;
  feedType: string;
  filter: string;
  query: string;
  root: string;
  team: string | null;
  top: number | null;
  enabled: boolean;
};

export type FeedDefinitionUpdatePayload = {
  query?: string;
  name?: string;
  enabled?: boolean;
};

/**
 * The result of running a definition's query against the live public API — the
 * same query + variables the feed assembler binds. An empty/`null` `data` is a
 * valid preview (the feed simply has no rows yet); `errors` is a refused query.
 */
export type FeedPreviewResult = {
  event: string;
  variables: Record<string, unknown>;
  endpoint: string;
  data: unknown;
  errors: unknown;
};

/**
 * One client's contract for one feed over one transport.
 *
 * `credentialRef` is an OPAQUE secret-store reference only (`secret://…`,
 * `env:…`, `arn:aws:secretsmanager:…`) — never a secret value. The API rejects
 * anything that is not a recognised reference.
 */
export type DeliveryAssignment = {
  id: string;
  clientId: string;
  packageId?: string | null;
  feedKey: string;
  deliveryType: string;
  destination: string;
  credentialRef?: string | null;
  format: string;
  lang: string;
  /** Never send more often than this. 0 = every change. */
  throttleSeconds: number;
  /** Send at least this often, changed or not. 0 = only when it changes. */
  scheduleSeconds: number;
  options: Record<string, unknown>;
  isActive: boolean;
};

export type DeliveryAssignmentCreatePayload = {
  clientId: string;
  packageId?: string | null;
  feedKey: string;
  deliveryType: string;
  destination: string;
  credentialRef?: string | null;
  format?: string;
  lang?: string;
  throttleSeconds?: number;
  scheduleSeconds?: number;
  options?: Record<string, unknown>;
  isActive?: boolean;
};

export type DeliveryAssignmentUpdatePayload = {
  packageId?: string | null;
  feedKey?: string;
  deliveryType?: string;
  destination?: string;
  credentialRef?: string | null;
  format?: string;
  lang?: string;
  throttleSeconds?: number;
  scheduleSeconds?: number;
  options?: Record<string, unknown>;
  isActive?: boolean;
};

/** What one client has of one file, and how its last send went. */
export type DeliveryStatusRow = {
  assignmentId: string;
  clientId: string;
  clientCode: string;
  feedKey: string;
  deliveryType: string;
  destination: string;
  isActive: boolean;
  throttleSeconds: number;
  scheduleSeconds: number;
  docKey?: string | null;
  lastSeq?: number | null;
  lastDeliveredAt?: string | null;
  lastAttemptAt?: string | null;
  status?: string | null;
  lastError?: string | null;
  deliveries: number;
  failures: number;
  lastBytes?: number | null;
  lastDurationMs?: number | null;
  lastTarget?: string | null;
  /** A newer version exists that this client has not had. */
  behind: boolean;
};

/** A delivery that gave up: kept so a person can look at it and try again. */
export type DeliveryDeadLetter = {
  id: string;
  assignmentId: string;
  clientId: string;
  feedKey: string;
  deliveryType: string;
  destination: string;
  docKey: string;
  seq: number;
  attempts: number;
  lastError: string;
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string | null;
};

/** How many files the sender's next pass will send because of an action. */
export type DeliverySendResult = { queued: number };

/** A client's per-feed reshape config — the declarative DSL, stored as data. */
export type ClientFeedTransform = {
  id: string;
  clientId: string;
  feedType: string;
  config: Record<string, unknown>;
  isActive: boolean;
};

export type ClientFeedTransformCreatePayload = {
  clientId: string;
  feedType: string;
  config?: Record<string, unknown>;
  isActive?: boolean;
};

export type ClientFeedTransformUpdatePayload = {
  config?: Record<string, unknown>;
  isActive?: boolean;
};
